import React, { useState, useCallback, useEffect } from "react";
import clsx from "clsx";
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  makeStyles,
  Theme,
  Tooltip,
  Typography
} from "@material-ui/core";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import * as FileApi from "../api";

const useStyles = makeStyles((theme: Theme) => ({
  image: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  link: {
    fontSize: theme.typography.pxToRem(15)
  },
  contain: {
    "& > img": {
      objectFit: "contain"
    }
  },
  modalImage: {
    maxWidth: "800px"
  }
}));

interface Props {
  fileId?: FileUploadId;
  fit?: "cover" | "contain";
  renderAsLink?: boolean;
  linkTitle?: string;
}

interface StateProps {
  isLoading: boolean;
  openModal: boolean;
  imageBlob?: string;
}

const ImageAvatar = (props: Props) => {
  const classes = useStyles();
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<StateProps>({
    isLoading: false,
    openModal: false
  });

  const handleOpenModal = () => setState(d => ({ ...d, openModal: true }));
  const handleCloseModal = () => setState(d => ({ ...d, openModal: false }));

  const fetchFile = useCallback(
    async (fileId: string) => {
      setState(s => ({ ...s, isLoading: true }));
      try {
        const response = await FileApi.download(fileId);
        const blob = await response.blob();
        const imageBlob = URL.createObjectURL(blob);
        setState({
          isLoading: false,
          openModal: false,
          imageBlob: imageBlob
        });
      } catch (e) {
        handleError(e);
        setState({ isLoading: false, openModal: false, imageBlob: undefined });
      }
    },
    [handleError]
  );

  useEffect(() => {
    if (props.fileId) {
      fetchFile(props.fileId);
    }
  }, [props.fileId, fetchFile]);

  return state.imageBlob ? (
    <>
      {props.renderAsLink ? (
        <Typography className={classes.link}>
          <Link href="#" onClick={handleOpenModal}>
            {props.linkTitle ?? "กดที่นี่เพื่อแสดงรูป"}
          </Link>
        </Typography>
      ) : (
        <Tooltip title="ดูรูปภาพ" placement="top">
          <div>
            <Button
              onClick={handleOpenModal}
              style={{ padding: 0, minWidth: 0 }}
            >
              <Avatar
                variant="rounded"
                src={state.imageBlob}
                className={clsx(
                  classes.image,
                  props.fit === "contain" && classes.contain
                )}
              />
            </Button>
          </div>
        </Tooltip>
      )}
      <Dialog maxWidth="xl" open={state.openModal} onClose={handleCloseModal}>
        <DialogContent>
          <img alt={""} src={state.imageBlob} className={classes.modalImage} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary" variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : (
    <Avatar variant="square">
      <ImageOutlinedIcon />
    </Avatar>
  );
};

export default ImageAvatar;
